import React, { useEffect, useMemo, useState } from 'react';
import Step1 from './Step1';
import type { FormData } from './MultiStepForm';

declare global {
  interface Window {
    __setWebhookPayload?: (payload: WebhookRenderPayload) => void;
    __WEBHOOK_RENDER_READY__?: boolean;
  }
}

export type WebhookRenderPayload = {
  formData: FormData;
  includedPages?: number[];
  excludedPages?: number[];
};

const A4_PX_WIDTH = 595;
const A4_PX_HEIGHT = 842;

function shouldInclude(pageNum: number, includedPages?: number[], excludedPages?: number[]) {
  if (excludedPages?.includes(pageNum)) return false;
  if (includedPages && includedPages.length > 0) return includedPages.includes(pageNum);
  return true;
}

const WebhookRender: React.FC = () => {
  const [payload, setPayload] = useState<WebhookRenderPayload | null>(null);

  useEffect(() => {
    window.__WEBHOOK_RENDER_READY__ = false;
    window.__setWebhookPayload = (p: WebhookRenderPayload) => setPayload(p);
    return () => {
      delete window.__setWebhookPayload;
    };
  }, []);

  const pages = useMemo(() => {
    if (!payload) return [];
    const { formData, includedPages, excludedPages } = payload;
    const out: Array<{ pageNum: number; node: React.ReactElement }> = [];

    const push = (pageNum: number, node: React.ReactElement) => {
      if (!shouldInclude(pageNum, includedPages, excludedPages)) return;
      out.push({ pageNum, node });
    };

    // For now, only generate page 1 through Step1 to match webhook requirement.
    push(
      1,
      React.createElement(Step1, {
        formData,
        updateFormData: () => {},
        isPDF: true,
        timelineCoverImage: formData.timelineCoverImage
      })
    );

    return out;
  }, [payload]);

  useEffect(() => {
    if (!payload) return;

    // Wait for images to load before signalling readiness.
    const waitForImages = async () => {
      const imgs = Array.from(document.images || []);
      await Promise.all(
        imgs.map(
          (img) =>
            new Promise<void>((resolve) => {
              if (img.complete) return resolve();
              img.onload = () => resolve();
              img.onerror = () => resolve();
            })
        )
      );
      // Small extra delay for fonts/layout
      await new Promise((r) => setTimeout(r, 200));
      window.__WEBHOOK_RENDER_READY__ = true;
    };

    waitForImages();
  }, [payload]);

  return (
    <div style={{ background: '#fff' }}>
      {/* Print CSS */}
      <style>{`
        @page { size: A4; margin: 0; }
        html, body { margin: 0; padding: 0; background: #fff; }
        @media print {
          html, body { margin: 0; padding: 0; }
        }
        .webhook-page {
          width: ${A4_PX_WIDTH}px;
          height: ${A4_PX_HEIGHT}px;
          overflow: hidden;
          page-break-after: always;
          break-after: page;
          background: white;
        }
      `}</style>

      {!payload ? (
        <div style={{ padding: 24, fontFamily: 'Inter, system-ui, sans-serif' }}>
          Waiting for webhook payload…
        </div>
      ) : (
        pages.map(({ pageNum, node }) => (
          <div className="webhook-page" key={pageNum} data-page={pageNum}>
            {node}
          </div>
        ))
      )}
    </div>
  );
};

export default WebhookRender;

